import { useEffect, useRef, useState } from "react";
import "./walkthrough.css";
import { analytics } from "./analytics.js";

const PAGE_TITLE = "Book an Apping Sales OS Walkthrough";
const PAGE_DESCRIPTION = "Tell Apping what you sell, where you want to grow and the sales problem you are trying to solve. Request a guided Apping Sales OS walkthrough.";
const EMAIL = "info@appingtechnology.com";

const navigation = [
  ["Product", "/product"],
  ["Market Entry", "/market-entry"],
  ["Who It’s For", "/who-its-for"],
  ["Growth", "/growth"],
  ["Insights", "/insights"],
  ["About", "/about"],
];

const walkthroughExamples = [
  "Entering a new market",
  "Identifying relevant companies",
  "Understanding a new sales territory",
  "Finding relevant stakeholders",
  "Improving account follow-up",
  "Carrying useful sales context forward",
  "Evaluating whether Sales OS fits an existing sales process",
];

const bring = [
  ["What do you sell?", "A short description of your product or service."],
  ["Where do you want to grow?", "A market, country, territory, industry or customer segment."],
  ["Who are you trying to reach?", "The type of businesses or buyers your sales team wants to develop."],
];

const exploreAreas = [
  "How the market can be approached",
  "What a focused company landscape may look like",
  "How account relevance can be considered",
  "How stakeholders fit into the sales context",
  "How outreach connects back to account intelligence",
  "How Sales OS could work alongside existing systems",
];

const nextSteps = [
  ["Share your objective", "Submit the walkthrough request."],
  ["Apping reviews the context", "We review the information provided before the conversation."],
  ["Walk through the problem", "The session focuses on the commercial objective and relevant Sales OS capabilities."],
  ["Decide whether to continue", "If there is a fit, the next step may involve guided configuration, scope alignment or an onboarding discussion."],
];

const contrasts = [
  ["A one-size-fits-all feature presentation", "A walkthrough shaped around the commercial problem"],
  ["An instant self-service signup or free trial", "A guided evaluation process"],
  ["A promise that every business is the right fit", "A conversation to understand whether Sales OS is relevant"],
  ["A custom AI-development consultation", "An evaluation of Apping Sales OS as a product"],
];

const fitStatements = [
  "Your team is entering a market it does not yet understand deeply",
  "You have many possible companies but limited sales capacity",
  "Several stakeholders may influence the buying decision",
  "Your team spends significant time researching accounts manually",
  "Sales knowledge is scattered between tools and people",
  "Your sales cycle depends on context, relationships and follow-up",
  "You want to understand whether Sales OS can support an existing sales process",
];

const pathways = [
  ["Product", "See what Sales OS does.", "/product"],
  ["Market Entry", "See how Sales OS approaches unfamiliar markets.", "/market-entry"],
  ["Sales Intelligence", "See how account context can build over time.", "/sales-intelligence"],
  ["Who It’s For", "See whether your sales environment fits the product.", "/who-its-for"],
  ["FAQ", "Read direct answers about product fit, availability and evaluation.", "/faq"],
];

function Header() {
  return (
    <header className="walk-header">
      <a className="walk-brand" href="/" aria-label="Apping Sales OS homepage"><img src="/apping-logo.png" alt="Apping Technology" width="985" height="999" /></a>
      <nav aria-label="Primary navigation">{navigation.map(([label, href]) => <a href={href} key={label}>{label}</a>)}</nav>
      <div className="walk-header-actions"><span aria-label="English language active">EN <i aria-hidden="true">| 简</i></span><a className="walk-button walk-button-small" href="#walkthrough-form">Book a walkthrough</a></div>
    </header>
  );
}

function Intro({ eyebrow, title, children, id }) {
  return <div className="walk-intro">{eyebrow ? <p className="walk-eyebrow">{eyebrow}</p> : null}<h2 id={id}>{title}</h2>{children ? <div className="walk-copy">{children}</div> : null}</div>;
}

function Footer() {
  return (
    <footer className="walk-footer">
      <a className="walk-footer-brand" href="/">Apping Sales OS</a>
<nav aria-label="Footer navigation"><a href="/product">Product</a><a href="/market-entry">Market Entry</a><a href="/sales-intelligence">Sales Intelligence</a><a href="/who-its-for">Who It’s For</a><a href="/growth">Apping Growth</a><a href="/insights">Insights</a><a href="/about">About</a><a href="/faq">FAQ</a><a href="/book-a-walkthrough">Book a Walkthrough</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/business-contact-data">Business Contact Data</a><a href="/privacy-request">Privacy Request</a></nav>
      <p>Apping Technology</p>
    </footer>
  );
}

function Field({ id, label, required = false, error, children, hint }) {
  return (
    <div className={`walk-field${error ? " has-error" : ""}`}>
      <label htmlFor={id}>{label}{required ? <span aria-hidden="true"> *</span> : <small> Optional</small>}</label>
      {children}
      {hint ? <p className="walk-field-hint" id={`${id}-hint`}>{hint}</p> : null}
      {error ? <p className="walk-field-error" id={`${id}-error`} role="alert">{error}</p> : null}
    </div>
  );
}

function WalkthroughForm() {
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState("");
  const [noticeType, setNoticeType] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const noticeRef = useRef(null);
  const formStarted = useRef(false);

  function handleFormStart() {
    if (formStarted.current) return;
    formStarted.current = true;
    analytics().trackEvent("walkthrough_form_start");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    analytics().trackEvent("walkthrough_form_submit");
    const form = event.currentTarget;
    const data = new FormData(form);
    const requiredFields = ["name", "email", "company", "offering", "market", "objective"];
    const nextErrors = {};
    requiredFields.forEach((name) => {
      if (!String(data.get(name) || "").trim()) nextErrors[name] = "Please complete this field.";
    });
    const email = String(data.get("email") || "").trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) nextErrors.email = "Enter a valid work email address.";
    setErrors(nextErrors);
    setNotice("");
    setNoticeType("");
    if (Object.keys(nextErrors).length) {
      const firstInvalid = form.querySelector(`[name="${Object.keys(nextErrors)[0]}"]`);
      firstInvalid?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: data.get("name"),
        workEmail: data.get("email"),
        company: data.get("company"),
        companyWebsite: data.get("website"),
        whatTheySell: data.get("offering"),
        targetMarket: data.get("market"),
        objective: data.get("objective"),
        phone: data.get("phone"),
        preferredContactMethod: data.get("contactMethod") || "no_preference",
        faxNumber: data.get("companyConfirm"),
      };
      const response = await fetch("/api/walkthrough", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({ ok: false }));
      if (!response.ok || result.accepted !== true || typeof result.requestId !== "string" || !result.requestId) throw new Error("SUBMISSION_FAILED");
      analytics().trackEvent("walkthrough_form_success");
      form.reset();
      setNoticeType("success");
      setNotice(`Thanks. We’ve received your walkthrough request and our team will follow up. Reference ID: ${result.requestId}`);
    } catch {
      setNoticeType("error");
      setNotice(`Your request was not submitted. Please email ${EMAIL} to request your walkthrough.`);
    } finally {
      setSubmitting(false);
      requestAnimationFrame(() => noticeRef.current?.focus());
    }
  }

  const describedBy = (id, hint = false) => [hint ? `${id}-hint` : "", errors[id] ? `${id}-error` : ""].filter(Boolean).join(" ") || undefined;

  return (
    <form className="walk-form" aria-label="Request Apping Sales OS walkthrough" data-clarity-mask="True" noValidate onFocusCapture={handleFormStart} onInputCapture={handleFormStart} onSubmit={handleSubmit}>
      <div className="walk-form-grid">
        <Field id="name" label="Name" required error={errors.name}><input id="name" name="name" type="text" autoComplete="name" required aria-invalid={Boolean(errors.name)} aria-describedby={describedBy("name")} /></Field>
        <Field id="email" label="Work email" required error={errors.email}><input id="email" name="email" type="email" inputMode="email" autoComplete="email" required aria-invalid={Boolean(errors.email)} aria-describedby={describedBy("email")} /></Field>
        <Field id="company" label="Company" required error={errors.company}><input id="company" name="company" type="text" autoComplete="organization" required aria-invalid={Boolean(errors.company)} aria-describedby={describedBy("company")} /></Field>
        <Field id="website" label="Company website"><input id="website" name="website" type="url" inputMode="url" autoComplete="url" placeholder="https://" /></Field>
        <Field id="offering" label="What does your company sell?" required error={errors.offering}><input id="offering" name="offering" type="text" required placeholder="e.g. Industrial packaging equipment for food manufacturers" aria-invalid={Boolean(errors.offering)} aria-describedby={describedBy("offering")} /></Field>
        <Field id="market" label="Market or territory" required error={errors.market}><input id="market" name="market" type="text" required placeholder="e.g. Malaysia, Southeast Asia, Hong Kong" aria-invalid={Boolean(errors.market)} aria-describedby={describedBy("market")} /></Field>
        <Field id="objective" label="What are you trying to achieve?" required error={errors.objective}><textarea id="objective" name="objective" rows="5" required placeholder="e.g. We want to identify established manufacturers that may be relevant buyers and understand who we should approach." aria-invalid={Boolean(errors.objective)} aria-describedby={describedBy("objective", true)} /><p className="walk-field-hint" id="objective-hint">Do not include passwords, sensitive data or detailed technical information.</p></Field>
        <div className="walk-optional-pair">
          <Field id="phone" label="Phone / WhatsApp"><input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" /></Field>
          <Field id="contact-method" label="Preferred contact method"><select id="contact-method" name="contactMethod" defaultValue=""><option value="">No preference</option><option value="email">Email</option><option value="phone">Phone</option><option value="whatsapp">WhatsApp</option></select></Field>
        </div>
      </div>
      <div className="walk-honeypot" aria-hidden="true"><label htmlFor="company-confirm">Leave this field empty</label><input id="company-confirm" name="companyConfirm" type="text" tabIndex="-1" autoComplete="off" /></div>
      <div className="walk-form-action">
        <button className="walk-button" type="submit" disabled={submitting}>{submitting ? "Submitting…" : "Request a walkthrough"}</button>
        <p>Required fields are marked with an asterisk.</p>
      </div>
      {notice ? <div className={`walk-form-notice is-${noticeType}`} role={noticeType === "error" ? "alert" : "status"} tabIndex="-1" ref={noticeRef}><strong>{noticeType === "success" ? "Request received." : "Request not sent."}</strong><p>{noticeType === "success" ? notice : <>Your request was not submitted. Please email <a href={`mailto:${EMAIL}?subject=Apping%20Sales%20OS%20walkthrough`}>{EMAIL}</a> to request a walkthrough.</>}</p></div> : null}
    </form>
  );
}

export default function WalkthroughPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content") || "";
    document.title = PAGE_TITLE;
    description?.setAttribute("content", PAGE_DESCRIPTION);
    document.documentElement.dataset.page = "walkthrough";
    window.__APPING_WALKTHROUGH_READY__ = true;
    return () => {
      document.title = previousTitle;
      description?.setAttribute("content", previousDescription);
      delete document.documentElement.dataset.page;
      delete window.__APPING_WALKTHROUGH_READY__;
    };
  }, []);

  return (
    <div className="walk-page">
      <a className="walk-skip" href="#walk-main">Skip to content</a>
      <Header />
      <main id="walk-main">
        <section className="walk-hero" aria-labelledby="walk-title">
          <div className="walk-container walk-hero-grid">
            <div className="walk-hero-copy">
              <p className="walk-eyebrow">Book a walkthrough</p>
              <h1 id="walk-title">Start with the market or sales problem you are trying to solve.</h1>
              <p>Tell us what you sell, where you want to grow and the type of customers your team is trying to reach. We’ll use the walkthrough to explore how Apping Sales OS may fit your sales environment.</p>
              <a className="walk-button" href="#walkthrough-form">Request a walkthrough</a>
            </div>
            <div className="walk-hero-context" aria-label="A walkthrough begins with your commercial objective">
              <figure className="walk-context-photo"><img src="/images/editorial/southeast-asia-business-district.webp" srcSet="/images/editorial/southeast-asia-business-district-800.webp 800w, /images/editorial/southeast-asia-business-district.webp 1600w" sizes="(max-width: 760px) 100vw, 38vw" alt="Singapore central business district across Marina Bay at dusk" width="1600" height="1067" loading="lazy" decoding="async" /><figcaption>Illustrative regional business environment.</figcaption></figure>
              <div className="walk-company-mark"><img src="/apping-logo.png" alt="" width="985" height="999" /><p><strong>Apping Technology</strong><span>Company behind Apping Sales OS</span></p></div>
              <blockquote>No generic demo script.<br />Start with your commercial objective.</blockquote>
              <div className="walk-objective-path"><span>What you sell</span><span>Where you want to grow</span><span>Who you need to reach</span><strong>Your walkthrough</strong></div>
            </div>
          </div>
        </section>

        <section className="walk-section walk-explanation" aria-labelledby="explanation-title">
          <div className="walk-container explanation-layout">
            <Intro title="A product walkthrough built around your sales problem." id="explanation-title"><p>Rather than showing every feature in isolation, the walkthrough starts with the commercial situation your team is working on.</p></Intro>
            <div className="walk-example-field" aria-label="Example walkthrough topics">{walkthroughExamples.map((item) => <span key={item}>{item}</span>)}</div>
            <blockquote>The goal is to understand whether Sales OS is relevant before discussing deeper configuration.</blockquote>
          </div>
        </section>

        <section className="walk-section walk-bring" aria-labelledby="bring-title">
          <div className="walk-container">
            <Intro eyebrow="What we need from you" title="Three things are enough to start." id="bring-title" />
            <ol className="bring-list">{bring.map(([title, body]) => <li key={title}><h3>{title}</h3><p>{body}</p></li>)}</ol>
            <p className="walk-support">You do not need to prepare a detailed technical brief.</p>
          </div>
        </section>

        <section className="walk-section walk-explore" aria-labelledby="explore-title">
          <div className="walk-container explore-layout">
            <Intro eyebrow="During the walkthrough" title="We’ll focus on the parts of Sales OS relevant to your objective." id="explore-title"><p>The discussion stays at the level needed to evaluate the product and your commercial situation.</p><p>You do not need to replace your CRM before evaluating Sales OS.</p></Intro>
            <ul className="explore-areas">{exploreAreas.map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        </section>

        <section className="walk-section walk-next" aria-labelledby="next-title">
          <div className="walk-container">
            <Intro title="A simple next step." id="next-title" />
            <ol className="next-sequence">{nextSteps.map(([title, body]) => <li key={title}><h3>{title}</h3><p>{body}</p></li>)}</ol>
          </div>
        </section>

        <section className="walk-section walk-expectations" aria-labelledby="expectations-title">
          <div className="walk-container expectations-layout">
            <Intro eyebrow="Clear expectations" title="Not a generic software demo." id="expectations-title" />
            <div className="contrast-list">{contrasts.map(([notThis, instead]) => <article key={notThis}><div><span>Not</span><p>{notThis}</p></div><div><span>Instead</span><p>{instead}</p></div></article>)}</div>
          </div>
        </section>

        <section className="walk-section walk-fit" aria-labelledby="fit-title">
          <div className="walk-container fit-layout">
            <Intro title="This walkthrough is most relevant if:" id="fit-title"><p>If several of these describe your sales environment, the walkthrough is likely a useful starting point.</p></Intro>
            <div className="fit-statements">{fitStatements.map((item) => <p key={item}>{item}</p>)}</div>
          </div>
        </section>

        <section className="walk-section walk-form-section" id="walkthrough-form" aria-labelledby="form-title">
          <div className="walk-container form-layout">
            <div className="walk-form-intro">
              <p className="walk-eyebrow">Request a walkthrough</p>
              <h2 id="form-title">Tell us about your sales objective.</h2>
              <p>Apping Technology uses this information to understand whether an Apping Sales OS walkthrough is relevant to your situation.</p>
              <aside><strong>What is not required</strong><p>No budget, procurement plan, technical architecture or sensitive company information is needed at this stage.</p></aside>
            </div>
            <div>
              <WalkthroughForm />
              <p className="walk-privacy-note">We use the information you provide to review and respond to your walkthrough request. See our <a href="/privacy">Privacy Policy</a>.</p>
            </div>
          </div>
        </section>

        <section className="walk-section walk-context" aria-labelledby="context-title">
          <div className="walk-container">
            <Intro title="Want to understand Sales OS first?" id="context-title" />
            <nav className="walk-pathways" aria-label="Learn more about Apping Sales OS">{pathways.map(([title, body, href]) => <a href={href} key={title}><h3>{title}</h3><p>{body}</p><span aria-hidden="true">↗</span></a>)}</nav>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
